import React, { useEffect, useRef, useState } from "react";
import { X, Check, LocateFixed, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

let loader = null;
const loadMaps = async () => {
  if (window.google?.maps?.Map) return window.google.maps;
  if (!loader) {
    loader = api.get("/solar/maps-key").then(({ data }) => new Promise((resolve, reject) => {
      window.__nurealMapsReady = () => resolve(window.google.maps);
      const s = document.createElement("script");
      s.src = `https://maps.googleapis.com/maps/api/js?key=${data.key}&callback=__nurealMapsReady&v=weekly&language=it`;
      s.async = true;
      s.onerror = () => { loader = null; reject(new Error("Google Maps non raggiungibile")); };
      document.head.appendChild(s);
    }));
  }
  return loader;
};

export const RelocateMap = ({ lat, lng, onConfirm, onClose }) => {
  const el = useRef(null);
  const map = useRef(null);
  const [center, setCenter] = useState({ lat, lng });
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    loadMaps().then((gm) => {
      if (!alive) return;
      map.current = new gm.Map(el.current, {
        center: { lat, lng }, zoom: 20, mapTypeId: "satellite", tilt: 0, heading: 0, gestureHandling: "greedy",
        streetViewControl: false, mapTypeControl: false, fullscreenControl: false, rotateControl: false, clickableIcons: false,
      });
      map.current.addListener("center_changed", () => { const c = map.current.getCenter(); setCenter({ lat: c.lat(), lng: c.lng() }); });
      setReady(true);
    }).catch((e) => setErr(e.message || formatApiError(e)));
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const locate = () => navigator.geolocation?.getCurrentPosition(
    (p) => { map.current?.setCenter({ lat: p.coords.latitude, lng: p.coords.longitude }); map.current?.setZoom(20); },
    () => toast.error("Posizione non disponibile"), { enableHighAccuracy: true, timeout: 10000 },
  );

  return (
    <div className="fixed inset-0 !mt-0 z-[80] bg-black flex flex-col" data-testid="relocate-map">
      <div className="flex items-center gap-2 p-2 bg-white">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold">Trova la casa giusta</div>
          <div className="text-[11px] truncate" style={{ color: "var(--text-muted)" }} data-testid="relocate-coords">Trascina la mappa e metti il mirino sul tetto · {center.lat?.toFixed(6)}, {center.lng?.toFixed(6)}</div>
        </div>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5" data-testid="relocate-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="relative flex-1">
        <div ref={el} className="absolute inset-0" data-testid="relocate-map-canvas" />
        {!ready && !err && <div className="absolute inset-0 flex items-center justify-center text-white"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        {err && <div className="absolute inset-0 flex items-center justify-center text-white text-sm p-6 text-center" data-testid="relocate-error">{err}</div>}
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-testid="relocate-crosshair">
          <div className="w-14 h-14 rounded-full border-2 border-white shadow-[0_0_0_2px_rgba(0,0,0,.5)]" />
          <div className="absolute left-1/2 top-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-500 border border-white" />
        </div>
        <button onClick={locate} className="absolute right-3 top-3 w-11 h-11 rounded-full bg-white shadow flex items-center justify-center" title="La mia posizione" data-testid="relocate-locate"><LocateFixed className="w-5 h-5" /></button>
      </div>
      <div className="p-3 bg-white flex gap-2" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
        <button onClick={onClose} className="btn-secondary flex-1 justify-center" data-testid="relocate-cancel">Annulla</button>
        <button onClick={() => onConfirm(center.lat, center.lng)} disabled={!ready} className="btn-primary flex-[2] justify-center" data-testid="relocate-confirm"><Check className="w-4 h-4" /> Usa questa casa</button>
      </div>
    </div>
  );
};
